import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { haversineDistance } from "@/lib/geo";
import { format, isPast, differenceInHours, startOfWeek, addWeeks, parseISO, differenceInMinutes } from "date-fns";
import { toast } from "sonner";
// Sprint B1b: customer-cancellation går via send-customer-email edge fn.
import type { CalendarEvent } from "@/components/calendar/WeekCalendar";
import type { Tables } from "@/integrations/supabase/types";
import { frozenDateTime } from "@/lib/notificationDisplay";

/** Booking row with joined courts + halls */
export interface BookingWithCourt extends Tables<"bookings"> {
  courts: {
    name: string;
    hall_id: string;
    halls: {
      name: string;
      cancellation_hours: number;
      checkin_opens_minutes: number | null;
      checkin_closes_minutes: number | null;
      latitude: number | null;
      longitude: number | null;
      checkin_radius_meters: number | null;
      access_code: string | null;
      access_code_send_minutes_before: number | null;
    } | null;
  } | null;
}

// UPPDRAG-30: type alias instead of empty-extending interface.
export type BookingPaymentRow = Tables<"booking_payments">;

export interface WaitlistRow extends Tables<"waitlist"> {
  courts: { name: string } | null;
}

interface TrainingSessionJoined {
  id: string;
  session_date: string;
  start_time: string;
  duration_minutes: number;
  status: string;
  court_id: string | null;
  training_groups: { name: string; color: string } | null;
  courts: { name: string } | null;
}

interface CoachSessionJoined extends TrainingSessionJoined {
  session_attendance: { id: string; rsvp_status: string }[];
}

interface AdminBookingJoined {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
  courts: { name: string; hall_id: string } | null;
}

interface CancellationTier {
  hours_before: number;
  percent: number;
}

interface CancellationSettings {
  late_cancel_allowed?: boolean;
  tiers?: CancellationTier[];
}

function getWeekStart(d: Date) {
  return startOfWeek(d, { weekStartsOn: 1 });
}

export type CheckinState =
  | { status: "checked_in"; time: Date }
  | { status: "too_early"; opensIn: number }
  | { status: "closed" }
  | { status: "open" };

export function useMyBookings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { roles, isAdmin } = useUserRole();
  const { organization } = useOrganization();
  const { hallId, hallHours, hall } = useOrgHall();
  const isCoach = roles.includes("coach");
  const [searchParams] = useSearchParams();

  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [rescheduleBooking, setRescheduleBooking] = useState<BookingWithCourt | null>(null);
  const [payingBookingId, setPayingBookingId] = useState<string | null>(null);
  const weekEndStr = format(addWeeks(weekStart, 1), "yyyy-MM-dd");
  const weekStartStr = format(weekStart, "yyyy-MM-dd");

  // Show success toast when returning from Stripe
  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      toast.success("Betalning genomförd! ✅");
    }
  }, [searchParams]);

  // --- Existing bookings list query ---
  const { data: bookings, isLoading } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("*, courts!inner(name, hall_id, halls(name, cancellation_hours, checkin_opens_minutes, checkin_closes_minutes, latitude, longitude, checkin_radius_meters, access_code, access_code_send_minutes_before))")
        .eq("user_id", user!.id)
        .is("deleted_at", null)
        .order("start_time", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Fetch payments for user's bookings
  const bookingIds = bookings?.map((b: BookingWithCourt) => b.id) ?? [];
  const { data: payments = [] } = useQuery({
    queryKey: ["my-booking-payments", bookingIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_payments")
        .select("*")
        .in("booking_id", bookingIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: bookingIds.length > 0,
  });

  const cancelMutation = useMutation({
    mutationFn: async (bookingId: string) => {
      const booking = bookings?.find((b: BookingWithCourt) => b.id === bookingId);
      // PR-AY1 (bug fix from strictNullChecks deep-dive §1.1): explicit null check
      // surfaces a useful diagnostic instead of crashing at line 147 when the
      // bookings cache is stale relative to the bookingId (other-tab cancel,
      // upstream filter mismatch, etc.). Previously: silent generic toast +
      // user retried forever.
      if (!booking) {
        toast.error("Bokningen hittades inte");
        throw new Error("booking_not_found");
      }

      // Check for late cancel charge for discounted members
      const hallData = booking.courts?.halls;
      const cancelHours = hallData?.cancellation_hours ?? 12;
      const hoursUntilStart = (new Date(booking.start_time).getTime() - Date.now()) / 3600000;

      if (hoursUntilStart < cancelHours && organization?.id) {
        try {
          const { getPlayerCategory } = await import("@/lib/playerPricing");
          const cat = await getPlayerCategory(user!.id, organization.id);

          const { data: catData } = await supabase
            .from("customer_categories")
            .select("late_cancel_charge_standard")
            .eq("slug", cat.slug)
            .eq("organization_id", organization.id)
            .maybeSingle();

          if ((catData as Record<string, unknown>)?.late_cancel_charge_standard && booking.standard_price_total) {
            // Create a pending payment for the standard price — Sprint D dual-write
            const amt = booking.standard_price_total;
            await supabase.from("booking_payments").insert({
              organization_id: organization.id,
              booking_id: bookingId,
              user_id: user!.id,
              amount: amt,                              // legacy kronor
              amount_minor: Math.round(amt * 100),      // öre
              currency: "SEK",
              payment_type: "full" as const,
              status: "pending" as const,
              metadata: { reason: "late_cancel_charge" },
            });
          }
        } catch {
          // Continue with cancellation even if charge fails
        }
      }

      // Calculate and store cancellation fee
      const cancellation = (organization?.settings?.cancellation ?? {}) as CancellationSettings;
      const tiers = cancellation.tiers;
      let feeData: Record<string, unknown> = {};
      if (tiers && tiers.length > 0 && booking) {
        const { calculateCancellationFee } = await import("@/lib/cancellationFee");
        const bookingPrice = booking.total_price ?? 0;
        const result = calculateCancellationFee(bookingPrice, new Date(booking.start_time), tiers);
        feeData = {
          cancelled_by: user!.id,
          cancellation_fee: result.fee,
          cancellation_fee_percent: result.percent,
        };
      }

      const { error } = await supabase
        .from("bookings")
        .update({ status: "cancelled" as const, ...feeData } as Tables<"bookings">)
        .eq("id", bookingId);
      if (error) throw error;

      // Trigger waitlist: offer to first person in queue.
      //
      // FEL-012a: detta körde tidigare en UPDATE mot en ANNAN användares köpost
      // direkt härifrån — alltså i den avbokande spelarens webbläsare. UPDATE-
      // policyerna på public.waitlist släpper bara igenom admin eller radens egen
      // ägare, så en vanlig medlem matchade NOLL rader. PostgREST svarade 204,
      // koden fångade aldrig { error }, och notisen skickades ändå. Kunden fick
      // "En tid har blivit ledig!" om en tid som aldrig markerats erbjuden.
      //
      // Nu går urvalet + UPDATE:n genom offer_waitlist_slot (SECURITY DEFINER,
      // org-gatad via get_user_org_ids). Den returnerar mottagarens user_id, och
      // NULL när kön var tom — notisen skickas ENDAST på icke-NULL.
      if (booking && organization?.id) {
        const bookingDate = format(new Date(booking.start_time), "yyyy-MM-dd");
        const bookingStartTime = format(new Date(booking.start_time), "HH:mm:ss");
        const courtName = booking.courts?.name ?? "Bana";
        const endTimeStr = format(new Date(booking.end_time), "HH:mm:ss");
        const offerMinutes = hall?.waitlist_offer_minutes ?? 15;

        const { data: offeredUserId, error: offerErr } = await supabase.rpc(
          "offer_waitlist_slot",
          {
            p_org: organization.id,
            p_court: booking.court_id,
            p_date: bookingDate,
            p_start: bookingStartTime,
            p_offer_minutes: offerMinutes,
          } as never,
        );

        if (offerErr) {
          // Sväljs inte längre. Avbokningen har redan lyckats — kön är en
          // sidoeffekt och får inte välta den — men felet ska synas.
          console.error("[waitlist] offer_waitlist_slot misslyckades:", offerErr);
        } else if (offeredUserId) {
          await supabase.from("notifications").insert({
            user_id: offeredUserId as unknown as string,
            organization_id: organization.id,  // PR-B: org-scope per Phase 1 RLS (already gated by `if (organization?.id)` above)
            type: "waitlist_offer",
            title: "En tid har blivit ledig!",
            body: `${courtName} ${frozenDateTime(new Date(booking.start_time))} är nu ledig. Du har ${offerMinutes} minuter att boka.`,
            // FEL-012c: utan action_url navigerar Notifications.tsx ingenstans
            // och notisen blir en återvändsgränd.
            action_url: "/book",
            data: {
              court_id: booking.court_id,
              date: bookingDate,
              start_time: bookingStartTime,
              end_time: endTimeStr,
            },
          });
        }
      }
    },
    onSuccess: (_data: unknown, bookingId: string) => {
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-bookings-upcoming"] });
      toast.success("Bokning avbokad");

      // Send cancellation email (fire-and-forget) via server-side dispatcher.
      // Sprint B1b: send-customer-email validerar att user äger bookingen,
      // hämtar template + render server-side. Inga client-supplied to/body.
      supabase.functions
        .invoke("send-customer-email", {
          body: { template: "booking_cancellation", bookingId },
        })
        .then(({ error }) => {
          if (error) console.error("[CANCEL EMAIL ERROR]", error);
        });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const upcoming = bookings?.filter(
    (b: BookingWithCourt) => b.status === "confirmed" && !isPast(new Date(b.start_time))
  );
  const past = bookings?.filter(
    (b: BookingWithCourt) => b.status !== "confirmed" || isPast(new Date(b.start_time))
  );

  const canCancel = (booking: BookingWithCourt) => {
    const cancellation = (organization?.settings?.cancellation ?? {}) as CancellationSettings;
    // With tiers: always cancellable if late_cancel_allowed is true
    if (cancellation.late_cancel_allowed !== false) return true;
    // Legacy fallback
    const hours = booking.courts?.halls?.cancellation_hours ?? 24;
    return differenceInHours(new Date(booking.start_time), new Date()) >= hours;
  };

  const handlePayNow = async (booking: BookingWithCourt) => {
    const bookingPayment = payments.find(
      (p: BookingPaymentRow) => p.booking_id === booking.id && p.user_id === user?.id && p.status === "pending"
    );
    if (!bookingPayment) {
      toast.error("Ingen obetald betalning hittades");
      return;
    }
    setPayingBookingId(booking.id);
    try {
      const baseUrl = window.location.origin;
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            booking_id: booking.id,
            amount: bookingPayment.amount,
            payment_id: bookingPayment.id,
            success_url: `${baseUrl}/bookings?payment=success`,
            cancel_url: `${baseUrl}/bookings`,
          },
        }
      );
      if (checkoutError) throw checkoutError;
      if (checkoutData?.url) {
        window.location.href = checkoutData.url;
        return;
      }
      toast.error("Kunde inte starta betalning");
    } catch (err: unknown) {
      console.error("Pay now error:", err);
      toast.error("Betalningsfel: " + (err instanceof Error ? err.message : "Okänt fel"));
    } finally {
      setPayingBookingId(null);
    }
  };

  const checkinMutation = useMutation({
    mutationFn: async (bookingId: string) => {
      const booking = bookings?.find((b: BookingWithCourt) => b.id === bookingId);
      const hallRef = booking?.courts?.halls;

      let checkinLat: number | undefined;
      let checkinLng: number | undefined;

      // If hall has coordinates, require geolocation verification
      if (hallRef?.latitude && hallRef?.longitude) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        });

        checkinLat = position.coords.latitude;
        checkinLng = position.coords.longitude;

        const distance = haversineDistance(
          checkinLat,
          checkinLng,
          hallRef.latitude,
          hallRef.longitude
        );

        const maxRadius = hallRef.checkin_radius_meters ?? 200;

        if (distance > maxRadius) {
          throw new Error(`Du är ${Math.round(distance)} meter från hallen (max ${maxRadius} m)`);
        }
      }

      const updatePayload: Record<string, unknown> = {
        checked_in_at: new Date().toISOString(),
      };
      if (checkinLat !== undefined) {
        updatePayload.checkin_latitude = checkinLat;
        updatePayload.checkin_longitude = checkinLng;
      }

      const { error } = await supabase
        .from("bookings")
        .update(updatePayload)
        .eq("id", bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      toast.success("Incheckad! ✅");
    },
    onError: (err: Error) => {
      if (err.message.includes("User denied") || err.message.includes("permission")) {
        toast.error("Tillåt platsåtkomst för att checka in");
      } else {
        toast.error(err.message);
      }
    },
  });

  const getCheckinState = (booking: BookingWithCourt): CheckinState => {
    const now = new Date();
    const start = new Date(booking.start_time);
    const opens = booking.courts?.halls?.checkin_opens_minutes ?? 15;
    const closes = booking.courts?.halls?.checkin_closes_minutes ?? 15;
    const minsUntilStart = differenceInMinutes(start, now);

    if (booking.checked_in_at) {
      return { status: "checked_in", time: new Date(booking.checked_in_at) };
    }
    if (minsUntilStart > opens) {
      return { status: "too_early", opensIn: minsUntilStart - opens };
    }
    if (minsUntilStart < -closes) {
      return { status: "closed" };
    }
    return { status: "open" };
  };

  // Waitlist query
  const { data: myWaitlist = [] } = useQuery({
    queryKey: ["my-waitlist", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("waitlist")
        .select("*, courts(name)")
        .eq("user_id", user!.id)
        .in("status", ["waiting", "offered"])
        .order("date");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });

  const leaveWaitlistMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("waitlist").update({ status: "expired" }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-waitlist"] });
      toast.success("Du har lämnat kön");
    },
  });

  // --- Calendar queries ---
  const { data: playerData, isLoading: playerLoading } = useQuery({
    queryKey: ["calendar-player", user?.id, weekStartStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select("session_id, rsvp_status, training_sessions!inner(id, session_date, start_time, duration_minutes, status, court_id, training_groups(name, color), courts(name))")
        .eq("player_id", user!.id)
        .gte("training_sessions.session_date", weekStartStr)
        .lt("training_sessions.session_date", weekEndStr)
        .neq("training_sessions.status", "cancelled");
      return data ?? [];
    },
    enabled: !!user && !isAdmin && !isCoach,
  });

  const { data: coachData, isLoading: coachLoading } = useQuery({
    queryKey: ["calendar-coach", user?.id, weekStartStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*, training_groups(name, color), courts(name), session_attendance(id, rsvp_status)")
        .eq("coach_id", user!.id)
        .gte("session_date", weekStartStr)
        .lt("session_date", weekEndStr)
        .neq("status", "cancelled");
      return data ?? [];
    },
    enabled: !!user && isCoach && !isAdmin,
  });

  const { data: adminSessions, isLoading: adminSessionsLoading } = useQuery({
    queryKey: ["calendar-admin-sessions", organization?.id, weekStartStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*, training_groups(name, color), courts(name)")
        .eq("organization_id", organization!.id)
        .gte("session_date", weekStartStr)
        .lt("session_date", weekEndStr)
        .neq("status", "cancelled");
      return data ?? [];
    },
    enabled: !!organization && isAdmin,
  });

  const { data: adminBookings } = useQuery({
    queryKey: ["calendar-admin-bookings", hallId, weekStartStr],
    queryFn: async () => {
      const wsISO = new Date(weekStartStr).toISOString();
      const weISO = new Date(weekEndStr).toISOString();
      const { data } = await supabase
        .from("bookings")
        .select("id, court_id, start_time, end_time, status, courts!inner(name, hall_id)")
        .eq("courts.hall_id", hallId!)
        .gte("start_time", wsISO)
        .lt("start_time", weISO)
        .neq("status", "cancelled");
      return data ?? [];
    },
    enabled: !!hallId && isAdmin,
  });

  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const result: CalendarEvent[] = [];
    const mapSession = (s: TrainingSessionJoined) => {
      const [h, m] = (s.start_time as string).split(":").map(Number);
      result.push({
        id: s.id,
        title: s.training_groups?.name ?? "Träning",
        subtitle: s.courts?.name ?? undefined,
        date: parseISO(s.session_date),
        startHour: h,
        startMinute: m,
        durationMinutes: s.duration_minutes,
        color: s.training_groups?.color ?? "#3b82f6",
        type: "session",
      });
    };
    if (!isAdmin && !isCoach && playerData) {
      playerData.forEach((a: { training_sessions: TrainingSessionJoined }) => mapSession(a.training_sessions));
    }
    if (isCoach && !isAdmin && coachData) {
      coachData.forEach((s: CoachSessionJoined) => {
        const attending = (s.session_attendance ?? []).filter((a: { id: string; rsvp_status: string }) => a.rsvp_status === "attending").length;
        const [h, m2] = (s.start_time as string).split(":").map(Number);
        result.push({
          id: s.id,
          title: s.training_groups?.name ?? "Träning",
          subtitle: `${attending} anmälda · ${s.courts?.name ?? ""}`,
          date: parseISO(s.session_date),
          startHour: h,
          startMinute: m2,
          durationMinutes: s.duration_minutes,
          color: s.training_groups?.color ?? "#3b82f6",
          type: "session",
        });
      });
    }
    if (isAdmin) {
      (adminSessions ?? []).forEach(mapSession);
      (adminBookings ?? []).forEach((b: AdminBookingJoined) => {
        const st = new Date(b.start_time);
        const et = new Date(b.end_time);
        const dur = (et.getTime() - st.getTime()) / 60000;
        result.push({
          id: b.id,
          title: "Bokning",
          subtitle: b.courts?.name ?? "",
          date: st,
          startHour: st.getHours(),
          startMinute: st.getMinutes(),
          durationMinutes: dur,
          color: "#6b7280",
          type: "booking",
        });
      });
    }
    return result;
  }, [playerData, coachData, adminSessions, adminBookings, isAdmin, isCoach]);

  const calLoading = playerLoading || coachLoading || adminSessionsLoading;

  return {
    // Auth / org
    user,
    hallId,
    hallHours,
    organization,

    // Bookings data
    bookings,
    isLoading,
    payments,
    upcoming,
    past,

    // Mutations & actions
    cancelMutation,
    canCancel,
    handlePayNow,
    payingBookingId,
    checkinMutation,
    getCheckinState,

    // Selection state
    selectedBookingId,
    setSelectedBookingId,
    rescheduleBooking,
    setRescheduleBooking,

    // Waitlist
    myWaitlist,
    leaveWaitlistMutation,

    // Calendar
    weekStart,
    setWeekStart,
    calendarEvents,
    calLoading,
  };
}
